import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import snipeHubService from '../../utils/services/snipeHubService';
import { BookmakerLogo, BOOKMAKER_DISPLAY_NAMES, bookmakerRank } from '../HotProps/bookmakerAssets';
import CountdownRing from './CountdownRing';

// Auto-refresh cadence (seconds) for live market availability.
const REFRESH_SECONDS = 60;

// Canonical column order for AFL player prop markets. Unknown markets are
// appended after these, alphabetically.
const MARKET_ORDER = [
    'Disposals',
    'Kicks',
    'Handballs',
    'Marks',
    'Tackles',
    'Goals',
    'Clearances',
    'Hit Outs',
    'Fantasy Points',
];

function bookmakerLabel(b) {
    return BOOKMAKER_DISPLAY_NAMES[b?.bookmaker] || b?.display_name || b?.bookmaker || '';
}

/**
 * Bookmaker × market availability matrix for a single match.
 *
 * Rows are bookmakers, columns are market categories; each cell marks whether
 * that book currently prices that market. Hydrated from the game-specific
 * Snipe Hub endpoint (`/afl/matches/:matchId`) so the availability reflects
 * the live odds feed rather than the (possibly stale) round-list payload.
 */
function MarketMatrix({ matchId }) {
    const [matchData, setMatchData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(REFRESH_SECONDS);

    const countdownRef = useRef(null);
    const refreshRef = useRef(null);

    // `background` refreshes hydrate silently: no spinner, and a transient
    // failure keeps the last good data on screen instead of wiping the table.
    const fetchMatch = useCallback(async ({ background = false } = {}) => {
        if (!matchId) {
            setIsLoading(false);
            return;
        }
        if (!background) {
            setIsLoading(true);
            setError(null);
        }

        const result = await snipeHubService.getAflMatch(matchId);

        if (result.success) {
            setMatchData(result.data?.matches?.[0] || null);
            setError(null);
        } else {
            // Stop polling when the failure can't be fixed by retrying.
            if (result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshRef.current) clearInterval(refreshRef.current);
            }
            if (!background) {
                setMatchData(null);
                setError(result);
            }
        }
        setIsLoading(false);
    }, [matchId]);

    // (Re)start the next-refresh wheel + the auto-refresh interval.
    const startTimers = useCallback(() => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshRef.current) clearInterval(refreshRef.current);
        setCountdown(REFRESH_SECONDS);
        countdownRef.current = setInterval(() => {
            setCountdown((prev) => (prev <= 1 ? REFRESH_SECONDS : prev - 1));
        }, 1000);
        refreshRef.current = setInterval(() => {
            fetchMatch({ background: true });
        }, REFRESH_SECONDS * 1000);
    }, [fetchMatch]);

    useEffect(() => {
        fetchMatch();
        startTimers();
        return () => {
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (refreshRef.current) clearInterval(refreshRef.current);
        };
    }, [fetchMatch, startTimers]);

    const handleRefresh = () => {
        fetchMatch();
        startTimers();
    };

    const books = useMemo(
        () => (Array.isArray(matchData?.bookmakers) ? matchData.bookmakers : []),
        [matchData]
    );

    // Union of every market offered across books, in canonical order.
    const markets = useMemo(() => {
        const seen = new Set();
        books.forEach((b) => (b.markets || []).forEach((m) => seen.add(m)));
        const known = MARKET_ORDER.filter((m) => seen.has(m));
        const unknown = [...seen].filter((m) => !MARKET_ORDER.includes(m)).sort();
        return [...known, ...unknown];
    }, [books]);

    // Canonical bookmaker order; unknown books rank after, alphabetically.
    const sortedBooks = useMemo(() => {
        return [...books].sort((a, b) =>
            bookmakerRank(a?.bookmaker) - bookmakerRank(b?.bookmaker)
                || bookmakerLabel(a).localeCompare(bookmakerLabel(b))
        );
    }, [books]);

    // Per-market column counts (how many books price each market).
    const columnCounts = useMemo(() => {
        const counts = {};
        markets.forEach((m) => {
            counts[m] = books.filter((b) => (b.markets || []).includes(m)).length;
        });
        return counts;
    }, [books, markets]);

    if (isLoading) {
        return (
            <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Market Availability</span>
                </div>
                <div className="flex items-center gap-2.5 py-10 justify-center">
                    <div className="w-4 h-4 border-2 border-slate-700 border-t-emerald-400 rounded-full animate-spin" />
                    <span className="text-xs font-mono text-slate-500">Loading market availability…</span>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Market Availability</span>
                </div>
                <div className="py-10 flex flex-col items-center text-center gap-3">
                    <span className="text-xs font-mono text-slate-500 max-w-sm leading-relaxed">
                        {error.message || 'Unable to load market availability for this game.'}
                    </span>
                    {['RATE_LIMIT_EXCEEDED', 'NETWORK_ERROR', 'SERVER_ERROR', 'SERVICE_UNAVAILABLE'].includes(error.errorCode) && (
                        <button
                            type="button"
                            onClick={handleRefresh}
                            className="px-3 py-1.5 text-[11px] font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                        >
                            Retry
                        </button>
                    )}
                </div>
            </section>
        );
    }

    if (books.length === 0) {
        return (
            <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Market Availability</span>
                </div>
                <div className="rounded-xl border border-slate-800/80 bg-slate-950/40 p-8 flex flex-col items-center text-center">
                    <div className="w-10 h-10 rounded-xl bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mb-3">
                        <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                    </div>
                    <p className="text-sm font-semibold text-white mb-1">No live odds yet</p>
                    <p className="text-xs font-mono text-slate-500 leading-relaxed max-w-xs">
                        Bookmaker markets will appear here once odds open for this match.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-slate-700/50 bg-slate-900/40 p-3 md:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">Market Availability</span>
                    <span className="text-[10px] font-mono text-slate-600">what each book is pricing</span>
                </div>
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
                        <span><span className="text-white font-semibold">{books.length}</span> {books.length === 1 ? 'book' : 'books'}</span>
                        <span className="text-slate-700">·</span>
                        <span><span className="text-white font-semibold">{markets.length}</span> {markets.length === 1 ? 'market' : 'markets'}</span>
                    </div>
                    {!isLoading && <CountdownRing countdown={countdown} total={REFRESH_SECONDS} />}
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono font-medium text-slate-400 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all disabled:opacity-50"
                    >
                        <svg className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto styled-scrollbar rounded-xl border border-slate-800/70">
                <table className="w-full text-sm border-separate" style={{ borderSpacing: 0 }}>
                    <thead>
                        <tr>
                            <th className="sticky left-0 z-10 bg-slate-900/95 backdrop-blur text-left text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 font-medium px-3 py-2.5 border-b border-slate-800/70 min-w-[120px]">
                                Bookmaker
                            </th>
                            {markets.map((m) => (
                                <th
                                    key={m}
                                    className="px-3 py-2.5 text-center border-b border-slate-800/70 bg-slate-900/40 whitespace-nowrap"
                                >
                                    <div className="text-[11px] font-mono font-semibold text-slate-300">{m}</div>
                                    <div className="text-[9px] font-mono text-slate-600 tabular-nums mt-0.5">
                                        {columnCounts[m]}/{books.length}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedBooks.map((b, rowIdx) => {
                            const offered = new Set(b.markets || []);
                            const isLast = rowIdx === sortedBooks.length - 1;
                            return (
                                <tr key={b.bookmaker} className="group">
                                    <td className={`sticky left-0 z-10 bg-slate-900/95 backdrop-blur px-3 py-2.5 whitespace-nowrap ${isLast ? '' : 'border-b border-slate-800/40'}`}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-md bg-slate-800 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                <BookmakerLogo bookmaker={b.bookmaker} size={24} />
                                            </div>
                                            <span className="text-[11px] font-medium text-slate-300 truncate">{bookmakerLabel(b)}</span>
                                            <span className="ml-auto pl-2 text-[9px] font-mono text-slate-600 tabular-nums">
                                                {offered.size}/{markets.length}
                                            </span>
                                        </div>
                                    </td>
                                    {markets.map((m) => {
                                        const has = offered.has(m);
                                        return (
                                            <td
                                                key={m}
                                                className={`px-3 py-2.5 text-center transition-colors group-hover:bg-slate-800/20 ${isLast ? '' : 'border-b border-slate-800/40'}`}
                                            >
                                                {has ? (
                                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-emerald-500/10 border border-emerald-500/25">
                                                        <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center justify-center w-5 h-5 text-slate-700 font-mono text-[11px]">—</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

export default MarketMatrix;
